import { mergeComponentValues } from './componentValues';

const asNumber = value => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};
const money = value => Math.round((value + Number.EPSILON) * 100) / 100;
const cleanImages = images => [...new Set((images || [])
  .filter(image => typeof image === 'string' && image.trim())
  .map(image => image.trim()))];

// Checkout interprets timestamps without an offset as UTC, as Firestore does.
const timestamp = value => {
  if (value instanceof Date) return value.getTime();
  if (typeof value !== 'string') return NaN;
  const normalized = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)
    ? `${value}Z`
    : value;
  return new Date(normalized).getTime();
};

export function getVariantStock(product, variantId = null) {
  const variant = product?.variants?.find(item => item.id === variantId);
  const stock = asNumber(variant?.stock_override ?? product?.stock);
  return Math.max(0, Math.floor(stock ?? 0));
}

export function getDefaultVariantId(product) {
  const available = (product?.variants || []).filter(variant => getVariantStock(product, variant.id) > 0);
  return available.length === 1 ? available[0].id : null;
}

/** Images belonging to the selected configuration, with generic product images as a fallback. */
export function getSelectionImages(product, variantId = null, components = {}) {
  if (!product) return [];
  const variant = product.variants?.find(item => item.id === variantId);
  if (variantId && !variant) return cleanImages(product.images);
  const selectedVariantId = variant?.id || null;
  const configured = new Map((product.components || []).map(component => [component.component_type_id, component]));
  const combinationImages = (product.combination_images || []).flatMap(entry => {
    const valueId = components?.[entry.component_type_id];
    const allowed = configured.get(entry.component_type_id)?.selected_values || [];
    return (entry.variant_id || null) === selectedVariantId && valueId &&
      valueId === entry.component_value_id && allowed.includes(valueId)
      ? entry.images || []
      : [];
  });
  const matched = cleanImages(combinationImages);
  if (matched.length) return matched;
  // Only product-specific choice photos belong in this product's gallery.
  // Shared catalog photos can be generic swatches and remain choice previews.
  const choiceImages = cleanImages((product.components || []).flatMap(config => {
    const valueId = components?.[config.component_type_id];
    if (!valueId || !(config.selected_values || []).includes(valueId)) return [];
    const customValue = product.custom_component_values?.[config.component_type_id]
      ?.find(value => value.id === valueId);
    return customValue?.images || [];
  }));
  if (choiceImages.length) return choiceImages;
  const variantImages = cleanImages(variant?.images);
  return variantImages.length ? variantImages : cleanImages(product.images);
}

/**
 * Resolve only options still configured on this product. Prices mirror checkout;
 * quantity discounts describe this one selection, excluding other cart lines.
 * componentTypes may contain catalog data or values already merged with overrides.
 */
export function resolveSelection(product, componentTypes = {}, {
  variantId = null, components = {}, quantity = 1, now = new Date(),
} = {}) {
  const variants = product?.variants || [];
  const variant = variants.find(item => item.id === variantId) || null;
  const variantRequired = variants.length > 0;
  const invalidVariant = Boolean(variantId) && !variant;
  const variantMissing = variantRequired && !variant;
  const rawComponents = components && typeof components === 'object' ? components : {};
  const validComponents = {};
  const componentSelections = [];
  const requiredMissing = [];
  const invalidSelections = [];
  const configured = product?.components || [];
  const componentGroups = configured.map(config => {
    const typeId = config.component_type_id;
    const catalog = componentTypes[typeId];
    const allowed = new Set(config.selected_values || []);
    const values = mergeComponentValues(catalog?.values, product?.custom_component_values?.[typeId])
      .filter(value => allowed.has(value.id))
      .map(value => ({ ...value, stock: asNumber(product?.component_stock?.[typeId]?.[value.id]) }));
    const selectedValue = values.find(value => value.id === rawComponents[typeId]) || null;
    const required = config.required !== false;
    const name = catalog?.name || config.component_type_name || 'Option';
    if (selectedValue) {
      validComponents[typeId] = selectedValue.id;
      componentSelections.push({
        component_type_id: typeId,
        component_type_name: name,
        value_id: selectedValue.id,
        value_name: selectedValue.name,
        price_modifier: asNumber(selectedValue.price_modifier) ?? 0,
      });
    } else {
      if (rawComponents[typeId]) invalidSelections.push(typeId);
      if (required) requiredMissing.push(typeId);
    }
    return { ...config, name, required, values, selectedValue };
  });
  const configuredIds = new Set(configured.map(config => config.component_type_id));
  Object.keys(rawComponents).forEach(typeId => {
    if (rawComponents[typeId] && !configuredIds.has(typeId)) invalidSelections.push(typeId);
  });

  let stock = getVariantStock(product, variant?.id);
  for (const selection of componentSelections) {
    const componentStock = asNumber(product?.component_stock?.[selection.component_type_id]?.[selection.value_id]);
    if (componentStock !== null) stock = Math.min(stock, Math.max(0, Math.floor(componentStock)));
  }

  // Zero is a real stock/modifier override. Checkout treats zero price overrides
  // as absent, so keep the displayed price consistent with the eventual charge.
  const variantPrice = asNumber(variant?.price_override);
  const basePrice = variantPrice > 0 ? variantPrice : asNumber(product?.price) ?? 0;
  const salePrice = asNumber(product?.sale_price);
  const currentTime = timestamp(now);
  const saleActive = Boolean(salePrice > 0 &&
    (!product?.sale_start_date || timestamp(product.sale_start_date) <= currentTime) &&
    (!product?.sale_end_date || timestamp(product.sale_end_date) >= currentTime));
  const variantSalePrice = asNumber(variant?.sale_price_override);
  const componentPrice = componentSelections.reduce((sum, selection) => sum + selection.price_modifier, 0);
  const originalPrice = money(basePrice + componentPrice);
  const unitPrice = money((saleActive ? (variantSalePrice > 0 ? variantSalePrice : salePrice) : basePrice) + componentPrice);
  const variantHasTiers = Boolean(variant?.quantity_discounts?.length);
  const tiers = [...(variantHasTiers ? variant.quantity_discounts : product?.quantity_discounts || [])]
    .filter(tier => Number.isInteger(Number(tier.min_qty)) && Number(tier.min_qty) >= 2 &&
      asNumber(tier.discount_value) > 0 && ['percent', 'fixed'].includes(tier.discount_type))
    .sort((left, right) => Number(left.min_qty) - Number(right.min_qty));
  const quantityValid = Number.isInteger(quantity) && quantity >= 1;
  const activeTier = quantityValid ? [...tiers].reverse().find(tier => quantity >= Number(tier.min_qty)) || null : null;
  const discountPerUnit = activeTier
    ? Math.min(Math.max(unitPrice, 0), activeTier.discount_type === 'percent'
      ? unitPrice * Number(activeTier.discount_value) / 100
      : Number(activeTier.discount_value))
    : 0;
  const quantityDiscount = quantityValid ? money(discountPerUnit * quantity) : 0;
  const discountedUnitPrice = money(unitPrice - discountPerUnit);
  const totalPrice = quantityValid ? money(unitPrice * quantity - quantityDiscount) : 0;
  const canPurchase = Boolean(product && product.active !== false && !variantMissing && !invalidVariant &&
    requiredMissing.length === 0 && invalidSelections.length === 0 && quantityValid &&
    stock >= quantity && unitPrice >= 0);

  return {
    variant, variantRequired, variantMissing, invalidVariant,
    validComponents, componentSelections, componentGroups, requiredMissing, invalidSelections,
    stock, quantityValid, canPurchase, basePrice, originalPrice, unitPrice, saleActive,
    tiers, variantHasTiers, activeTier, discountedUnitPrice, quantityDiscount, totalPrice,
    images: getSelectionImages(product, variantId, validComponents),
  };
}
